import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Monitor, Smartphone, Loader2, Mail, Info, Zap, Hand } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/sales-settings/email-settings`;
const auth = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` } });

// Modal "como é o e-mail que a pessoa recebe": assunto, destinatário e o HTML real renderizado num iframe (desktop/celular)
export const EmailPreviewDialog = ({ type, language = 'pt-BR', onClose }) => {
  const en = language === 'en';
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    if (!type) return undefined;
    let alive = true;
    setData(null); setErr(null);
    axios.get(`${API}/${type.key}/preview`, { ...auth(), params: { lang: language } })
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(e.response?.data?.detail || (en ? 'Could not load the preview' : 'Não foi possível carregar a prévia')); });
    return () => { alive = false; };
  }, [type, language, en]);

  if (!type) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl w-[96vw] max-h-[94vh] p-0 overflow-hidden flex flex-col" data-testid="email-preview-dialog">
        <DialogHeader className="px-4 pt-4 pb-3 border-b space-y-2">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Mail className="h-4 w-4 text-orange-600" /> {en ? type.en : type.pt}
            <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${type.auto ? 'bg-sky-100 text-sky-800' : 'bg-slate-100 text-slate-700'}`}>{type.auto ? <Zap className="h-3 w-3" /> : <Hand className="h-3 w-3" />}{type.auto ? 'auto' : 'manual'}</span>
          </DialogTitle>
          <p className="text-[11px] text-slate-500 -mt-1">{en ? 'Example of the email the recipient gets (same template as the real one, sample data).' : 'Exemplo do e-mail que a pessoa recebe (mesmo modelo do envio real, com dados fictícios).'}</p>
          {data && (
            <div className="rounded-md border bg-slate-50 px-3 py-2 text-xs text-slate-700 space-y-0.5" data-testid="email-preview-meta">
              <div><span className="text-slate-500">{en ? 'To' : 'Para'}:</span> <b>{data.to}</b></div>
              <div><span className="text-slate-500">{en ? 'Subject' : 'Assunto'}:</span> <b data-testid="email-preview-subject">{data.subject}</b></div>
              {data.note && <div className="flex items-start gap-1 text-amber-800" data-testid="email-preview-note"><Info className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {data.note}</div>}
            </div>
          )}
          <div className="flex items-center gap-1 self-start rounded-lg bg-slate-100 p-0.5">
            <button type="button" onClick={() => setMobile(false)} aria-pressed={!mobile} className={`h-7 px-2.5 rounded-md text-[11px] font-semibold inline-flex items-center gap-1 ${!mobile ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`} data-testid="email-preview-desktop"><Monitor className="h-3.5 w-3.5" /> Desktop</button>
            <button type="button" onClick={() => setMobile(true)} aria-pressed={mobile} className={`h-7 px-2.5 rounded-md text-[11px] font-semibold inline-flex items-center gap-1 ${mobile ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`} data-testid="email-preview-mobile"><Smartphone className="h-3.5 w-3.5" /> {en ? 'Phone' : 'Celular'}</button>
          </div>
        </DialogHeader>
        <div className="flex-1 min-h-0 bg-slate-200/70 flex justify-center overflow-hidden p-3">
          {err ? <p className="text-sm text-red-600 self-center" data-testid="email-preview-error">{err}</p>
            : !data ? <div className="self-center flex items-center gap-2 text-sm text-slate-600" data-testid="email-preview-loading"><Loader2 className="h-4 w-4 animate-spin" /> {en ? 'Rendering…' : 'Montando o e-mail…'}</div>
            : <iframe title="email preview" sandbox="" srcDoc={data.html} className={`h-full bg-white rounded-lg shadow-lg border-0 transition-[width] duration-200 ${mobile ? 'w-[390px]' : 'w-full'}`} style={{ minHeight: '60vh' }} data-testid="email-preview-frame" data-mode={mobile ? 'mobile' : 'desktop'} />}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailPreviewDialog;
